import { Icon } from "@iconify/react/dist/iconify.js";
import { Button, Card, Drawer, Flex, Form, Grid, Input, message, Pagination, Select, Table } from "antd";
import { useEffect, useState } from "react";
import useDebounce from "../../hooks/useDebounce";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { complaintCategoryService } from "../../services/complaint-category.service";
import TableAction from "../../components/TableAction";
import { useTableHeight } from "../../hooks/useTableHeight";

export default function ComplaintCategory() {

  const breakpoint = Grid.useBreakpoint()
  const [form] = Form.useForm()
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [messageApi, contextHolder] = message.useMessage();
  const tableHeight = useTableHeight();
  const queryClient = useQueryClient();

  const [drawerOpt, setDrawerOpt] = useState({
    open: false,
    id: null
  })

  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    query: null
  })

  useEffect(() => {
    setDataParams(prev => ({
      ...prev,
      query: debouncedKeyword,
      page: 1
    }))
  }, [debouncedKeyword])

  const { data: categoriesData, isLoading, isRefetching } = useQuery({
    queryKey: ['categories', dataParams],
    queryFn: () => complaintCategoryService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  })

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page
    })
  }

  const storeMutation = useMutation({
    mutationFn: val => complaintCategoryService.store(val),
    onSuccess: () => {
      queryClient.invalidateQueries('categories');
      messageApi.success('Data berhasil ditambah');
      handleCloseDrawerOpt()
    },
    onError: (error) => {
      console.log(error)
      messageApi.error('Terjadi kesalahan');
    }
  })

  const updateMutation = useMutation({
    mutationFn: val => complaintCategoryService.update(val),
    onSuccess: () => {
      queryClient.invalidateQueries('categories');
      messageApi.success('Data berhasil diubah');
      handleCloseDrawerOpt()
    },
    onError: (error) => {
      console.log(error)
      messageApi.error('Terjadi kesalahan');
    }
  })

  const deleteMutation = useMutation({
    mutationFn: val => complaintCategoryService.delete(val),
    onSuccess: () => {
      queryClient.invalidateQueries('categories');
      messageApi.success('Data berhasil dihapus');
      handleCloseDrawerOpt()
    },
    onError: (error) => {
      console.log(error)
      messageApi.error('Terjadi kesalahan');
    }
  })

  const handleCloseDrawerOpt = () => {
    form.resetFields()
    setDrawerOpt({ open: false, id: null })
  }

  const handleSubmit = val => {
    if (drawerOpt.id) updateMutation.mutate({ id: drawerOpt.id, ...val })
    else storeMutation.mutate(val)
  }

  const handleEdit = record => {
    form.setFieldsValue(record)
    setDrawerOpt({ open: true, id: record.id })
  }

  const columns = [
    {
      title: 'No',
      align: 'center',
      width: 80,
      render: (text, record, index) => index + 1,
      fixed: breakpoint.md && 'left'
    },
    {
      title: 'Kategori',
      dataIndex: 'name',
      width: 180,
    },
    {
      title: 'Jenis Aduan',
      dataIndex: 'type',
      width: 180,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      width: 180,
    },
    {
      title: 'Sub-value',
      dataIndex: 'subvalue',
      width: 180,
    },
    {
      className: 'last-cell-p',
      title: 'Aksi',
      align: 'right',
      width: 120,
      fixed: 'right',
      render: record => (
        <TableAction
          onClickEdit={() => handleEdit(record)}
          handleDelete={() => deleteMutation.mutate(record.id)}
          deleteLoading={deleteMutation.isPending}
        />
      )
    },
  ]

  return (
    <>
      {contextHolder}
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0
          }
        }}
      >
        <Flex justify="space-between" gap={20} style={{ paddingInline: 24, marginBottom: 24 }} >
          <Input
            style={{ width: 320 }}
            prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
            placeholder="cari..."
            onChange={e => setKeyword(e.target.value)}
            value={keyword}
          />
          <Button
            variant="solid"
            color="primary"
            icon={<Icon icon="duo-icons:add-circle" width="22" height="22" />}
            onClick={() => setDrawerOpt({ open: true, id: null })}
            style={{ marginLeft: !breakpoint.md && 'auto', minWidth: 38 }}
          >
            {breakpoint.md && 'Tambah Kategori'}
          </Button>
        </Flex>
        <Table
          columns={columns}
          pagination={false}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={'id'}
          scroll={{ y: tableHeight }}
          dataSource={categoriesData?.data}
        />
        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={categoriesData?.pagination?.total}
          showSizeChanger={{
            showSearch: false,
          }}
          pageSizeOptions={['10', '20', '50']}
          current={categoriesData?.pagination?.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
      <Drawer
        open={drawerOpt.open}
        title={drawerOpt.id ? "Ubah Data" : "Tambah Data"}
        onClose={handleCloseDrawerOpt}
        footer={
          <Flex gap={16} >
            <Button
              block
              // disabled={storeMutation.isPending} 
              // loading={storeMutation.isPending} 
              variant="outlined"
              color="primary"
            >
              Batal
            </Button>
            <Button
              block
              variant="solid"
              color="primary"
              onClick={() => form.submit()}
            // disabled={storeMutation.isPending} 
            // loading={storeMutation.isPending} 
            >
              Simpan
            </Button>
          </Flex>
        }
      >
        <Form
          layout="vertical"
          onFinish={handleSubmit}
          form={form}
        >
          <Form.Item rules={[{ required: true }]} name={'name'} label="Kategori" >
            <Input />
          </Form.Item>
          <Form.Item rules={[{ required: true }]} name={'type'} label="Jenis Aduan" >
            <Select
              options={['Pelanggan', 'Non Pelanggan'].map(e => ({ label: e, value: e }))}
            />
          </Form.Item>
          <Form.Item rules={[{ required: true }]} name={'value'} label="Value" >
            <Select
              options={['CMP', 'REQ'].map(e => ({ label: e, value: e }))}
              onChange={(val) => form.setFieldsValue({ subvalue: `${val}-` })}
            />
          </Form.Item>
          <Form.Item rules={[{ required: true }]} name={'subvalue'} label="Sub-value" >
            <Input />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  )
}